<template>
  <div class="pm-cols">
    <div class="pm-card">
      <div class="pm-card__head">
        <h3>{{ $t('projects.management.running_now') }}</h3>
        <span class="pm-card__sub">{{ $t('projects.management.sorted_by_due') }}</span>
        <span class="grow"></span>
        <div class="pm-seg pm-seg--sm">
          <div :class="{'on': !onlyMine}" @click="onlyMine = false" qid="pm-running-all">{{ $t('projects.management.all') }}</div>
          <div :class="{'on': onlyMine}" @click="onlyMine = true" qid="pm-running-mine">{{ $t('projects.management.mine') }}</div>
        </div>
      </div>
      <table class="pm-table">
        <thead>
          <tr>
            <th style="width:46%">{{ $t('projects.management.columns.project') }}</th>
            <th>{{ $t('projects.management.columns.progress') }}</th>
            <th>{{ $t('projects.management.columns.due') }}</th>
            <th>{{ $t('projects.management.columns.team') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="4" class="text-muted"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</td></tr>
          <tr v-if="!loading && !visibleRunning.length"><td colspan="4" class="text-muted">{{ $t('projects.management.nothing_running') }}</td></tr>
          <tr v-for="project in visibleRunning.slice(0, 8)" :key="project.slug" qid="pm-running-row">
            <td>
              <div class="pm-name">
                <span class="pm-icon" :class="'pm-c-' + meta(project.type).color"><font-awesome-icon :icon="meta(project.type).icon"/></span>
                <div class="grow">
                  <router-link :to="{name: 'projects.view', params: {slug: project.slug}}" class="pm-name__title">{{ project.title }}</router-link>
                  <div class="pm-name__ctx">
                    {{ $t('projects.management.types.' + project.type) }}
                    <template v-if="project.project && project.project.slug">
                      · {{ $t('projects.management.round_of') }}
                      <router-link :to="{name: 'projects.view', params: {slug: project.project.slug}}">{{ project.project.title }}</router-link>
                    </template>
                  </div>
                </div>
              </div>
            </td>
            <td><ProgressBar :value="project.progress" :tone="isOverdue(project) ? 'red' : ''"/></td>
            <td class="pm-due" :class="{'pm-due--red': isOverdue(project)}">{{ dueText(project) }}</td>
            <td><Team :users="project.users"/></td>
          </tr>
        </tbody>
      </table>
      <div class="pm-card__foot" v-if="visibleRunning.length > 8">
        {{ $t('projects.management.more_running', {count: visibleRunning.length - 8}) }} ·
        <span class="pm-link" @click="$emit('open-view', 'list')">{{ $t('projects.management.see_all_projects') }}</span>
      </div>
    </div>

    <div class="pm-side">
      <div class="pm-card">
        <div class="pm-card__head">
          <h3>{{ $t('projects.management.needs_attention') }}</h3>
          <span class="grow"></span>
          <span class="pm-tag pm-tag--red" v-if="attention.length">{{ attention.length }}</span>
        </div>
        <div class="pm-empty" v-if="!loading && !attention.length">{{ $t('projects.management.all_clear') }}</div>
        <div class="pm-att"
             v-for="item in attention.slice(0, 6)"
             :key="item.kind + item.project.slug"
             qid="pm-attention-row">
          <span class="pm-icon pm-icon--sm" :class="'pm-c-' + item.color"><font-awesome-icon :icon="item.icon"/></span>
          <div class="grow">
            <router-link :to="{name: 'projects.view', params: {slug: item.project.slug}}" class="pm-att__title">{{ item.project.title }}</router-link>
            <div class="pm-att__text">{{ item.text }}</div>
          </div>
          <span class="pm-link"
                v-if="item.kind === 'overdue'"
                @click="remind(item.project)"
                qid="pm-attention-remind">{{ $t('projects.management.remind') }}</span>
          <span class="pm-link"
                v-if="item.kind === 'due'"
                @click="$emit('run', item.project)"
                qid="pm-attention-run">{{ $t('projects.launcher.run') }}</span>
          <router-link v-if="item.kind === 'done'" class="pm-link" :to="{name: 'projects.view', params: {slug: item.project.slug}}">{{ $t('projects.management.open') }}</router-link>
        </div>
      </div>

      <div class="pm-card">
        <div class="pm-card__head">
          <h3>{{ $t('projects.management.coming_up') }}</h3>
          <span class="pm-card__sub">{{ $t('projects.management.next_days', {count: 60}) }}</span>
          <span class="grow"></span>
          <span class="pm-link" @click="$emit('open-view', 'year')">{{ $t('projects.management.views.year') }}</span>
        </div>
        <div class="pm-empty" v-if="!loading && !upcoming.length">{{ $t('projects.management.nothing_planned') }}</div>
        <div class="pm-upcoming">
          <div class="pm-up"
               v-for="(item, index) in upcoming.slice(0, 6)"
               :key="index"
               :class="{'pm-up--plan': item.planned}">
            <div class="pm-up__date">{{ item.date.format('MMM') }}<b>{{ item.date.format('D') }}</b></div>
            <div class="grow">
              <router-link :to="{name: 'projects.view', params: {slug: item.slug}}" class="pm-up__title">{{ item.title }}</router-link>
              <div class="pm-up__text">{{ item.text }}</div>
            </div>
            <span class="pm-tag" :class="'pm-tag--' + item.tagTone" v-if="item.tag">{{ item.tag }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { typeMeta } from '@/views/Projects/Management/projectTypes.js'
  import ProgressBar from '@/views/Projects/Management/ProgressBar.vue';
  import Team from '@/views/Projects/Management/Team.vue';

  // Overview of the Project management page: what runs now, what needs attention and what comes next (US 9396)
  export default {
    name: 'ProjectsOverview',
    components: {
      ProgressBar,
      Team
    },
    data: function() {
      return {
        loading: true,
        onlyMine: false,
        running: [],
        recurring: [],
        plan: {projects: [], planned: []}
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      },
      visibleRunning() {
        let self = this;
        if (!this.onlyMine) {
          return this.running;
        }
        return _.filter(this.running, function(project) {
          return _.some(project.users, {id: self.getUser.id});
        });
      },
      attention() {
        let self = this;
        var moment = require('moment');
        let items = [];
        _.forEach(this.running, function(project) {
          if (self.isOverdue(project)) {
            items.push({kind: 'overdue', project: project, icon: 'exclamation-circle', color: 'rose',
              text: self.$tc('projects.management.overdue_days', self.overdueDays(project), {count: self.overdueDays(project)})});
          }
        });
        _.forEach(this.recurring, function(project) {
          if (project.run_due) {
            items.push({kind: 'due', project: project, icon: 'repeat', color: 'amber', text: self.$t('projects.management.round_due')});
          }
        });
        _.forEach(this.running, function(project) {
          if (!self.isOverdue(project) && project.progress >= 100) {
            items.push({kind: 'done', project: project, icon: 'check', color: 'grey', text: self.$t('projects.management.ready_to_close')});
          }
        });
        return items;
      },
      upcoming() {
        let self = this;
        var moment = require('moment');
        let today = moment().startOf('day');
        let items = [];
        _.forEach(this.plan.planned, function(run) {
          items.push({
            date: moment(run.date), slug: run.slug, title: run.title, planned: true,
            text: run.due ? self.$t('projects.launcher.due_now') : (run.auto_create ? self.$t('projects.management.planned_auto') : self.$t('projects.management.planned_manual')),
            tag: run.due ? self.$t('projects.management.due') : (run.auto_create ? self.$t('projects.management.auto') : self.$t('projects.management.manual')),
            tagTone: run.due ? 'red' : (run.auto_create ? 'teal' : 'grey')
          });
        });
        _.forEach(this.plan.projects, function(project) {
          let start = moment(project.start_date);
          if (start.isAfter(today)) {
            items.push({date: start, slug: project.slug, title: project.title, text: self.$t('projects.management.starts')});
          } else if (project.end_date && project.status === 'in progress' && !moment(project.end_date).isBefore(today)) {
            items.push({date: moment(project.end_date), slug: project.slug, title: project.title, text: self.$t('projects.management.due_date')});
          }
        });
        return _.sortBy(items, function(item) {
          return item.date.valueOf();
        });
      }
    },
    methods: {
      meta(type) {
        return typeMeta(type);
      },
      isOverdue(project) {
        var moment = require('moment');
        return !!project.end_date && moment(project.end_date).endOf('day').isBefore(moment());
      },
      overdueDays(project) {
        var moment = require('moment');
        return moment().startOf('day').diff(moment(project.end_date).startOf('day'), 'days');
      },
      dueText(project) {
        var moment = require('moment');
        if (!project.end_date) {
          return '—';
        }
        if (this.isOverdue(project)) {
          return this.$tc('projects.management.overdue_short', this.overdueDays(project), {count: this.overdueDays(project)});
        }
        return moment(project.end_date).format('D MMM');
      },
      remind(project) {
        piincHttp.get('projects/' + project.slug + '/reminders').then(() => {
          this.$toastr('success', this.$t('projects.create.project_reminders_success'));
        }, () => {});
      },
      emitStats() {
        var moment = require('moment');
        let self = this;
        let monthEnd = moment().endOf('month');
        this.$emit('stats', {
          running: this.running.length,
          overdue: _.filter(this.running, function(project) { return self.isOverdue(project); }).length,
          dueThisMonth: _.filter(this.running, function(project) {
            return project.end_date && !self.isOverdue(project) && !moment(project.end_date).isAfter(monthEnd);
          }).length,
          recurring: this.recurring.length,
          attention: this.attention.length,
          planned: _.filter(this.plan.planned, function(run) { return !run.due; }).length
        });
      },
      loadContent() {
        var moment = require('moment');
        let client = this.getClient.slug;
        this.loading = true;
        Promise.all([
          piincHttp.get('projects', { params: { client: client, control_type: 'one time', status: 'in progress', per_page: 100, sort: 'end_date', order: 'asc' } }),
          piincHttp.get('projects', { params: { client: client, control_type: 'continuous', without_parent: 1, per_page: 100 } }),
          piincHttp.get('projects/year-plan', { params: { client: client, from: moment().format('YYYY-MM-DD'), to: moment().add(60, 'days').format('YYYY-MM-DD') } })
        ]).then((responses) => {
          // Projects without a due date go last
          this.running = _.sortBy(responses[0].data.items, function(project) {
            return project.end_date ? project.end_date : '9999';
          });
          this.recurring = responses[1].data.items;
          this.plan = responses[2].data;
          this.loading = false;
          this.emitStats();
        }, () => {
          this.loading = false;
        });
      }
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.loadContent();
      }
    }
  }
</script>
